import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useLLMDiagnose } from '../contexts/LLMDiagnoseContext';
import { useWorkContext } from '../contexts/WorkContext';
import API from '../api/ApiLayer';
import Markdown from 'markdown-to-jsx';
import { useBreedsContext } from '../contexts/BreedsContext';
import { useAnimalTypesContext } from '../contexts/AnimalTypesContext';
import { useDiagnosesPage } from '../contexts/DiagnosesPageContext';
import { useSymptoms } from '../contexts/SymptomsContext';
import { useTreatmentsPage } from '../contexts/TreatmentsPageContext';
import moment from 'moment';
import {
  ContentCopy,
  Download,
  Edit,
  Print,
  Refresh,
  Save,
  Cancel,
} from '@mui/icons-material';
import html2pdf from 'html2pdf.js';
import { marked } from 'marked';
import { useDosagesPage } from '../contexts/DosagesPageContext';
import { TreatmentType } from './diseases-and-treatments/types';
import { getDrugDosages } from '../utils/summaryUtils';
import { useDoseParams } from '../contexts/DoseParamsContext';
import { useSoapNotes } from '../contexts/SoapNotesContext';

// Use marked to convert markdown to HTML
// Configure marked for GitHub Flavored Markdown if needed
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Language options for SOAP notes generation (same as ambient listening)
const awsTranscribeLanguages = [
  { code: 'ab-GE', name: 'Abkhaz' },
  { code: 'af-ZA', name: 'Afrikaans' },
  { code: 'ar-AE', name: 'Arabic, Gulf' },
  { code: 'ar-SA', name: 'Arabic, Modern Standard' },
  { code: 'hy-AM', name: 'Armenian' },
  { code: 'ast-ES', name: 'Asturian' },
  { code: 'az-AZ', name: 'Azerbaijani' },
  { code: 'ba-RU', name: 'Bashkir' },
  { code: 'eu-ES', name: 'Basque' },
  { code: 'be-BY', name: 'Belarusian' },
  { code: 'bn-IN', name: 'Bengali' },
  { code: 'bs-BA', name: 'Bosnian' },
  { code: 'bg-BG', name: 'Bulgarian' },
  { code: 'ca-ES', name: 'Catalan' },
  { code: 'ckb-IR', name: 'Central Kurdish, Iran' },
  { code: 'ckb-IQ', name: 'Central Kurdish, Iraq' },
  { code: 'zh-HK', name: 'Chinese (Cantonese), Hong-Kong' },
  { code: 'zh-CN', name: 'Chinese, Simplified' },
  { code: 'zh-TW', name: 'Chinese, Traditional' },
  { code: 'hr-HR', name: 'Croatian' },
  { code: 'cs-CZ', name: 'Czech' },
  { code: 'da-DK', name: 'Danish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'en-AU', name: 'English, Australian' },
  { code: 'en-GB', name: 'English, British' },
  { code: 'en-IN', name: 'English, Indian' },
  { code: 'en-IE', name: 'English, Irish' },
  { code: 'en-NZ', name: 'English, New Zealand' },
  { code: 'en-AB', name: 'English, Scottish' },
  { code: 'en-ZA', name: 'English, South African' },
  { code: 'en-US', name: 'English, US' },
  { code: 'en-WL', name: 'English, Welsh' },
  { code: 'et-ET', name: 'Estonian' },
  { code: 'fa-IR', name: 'Farsi' },
  { code: 'fi-FI', name: 'Finnish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'fr-CA', name: 'French, Canadian' },
  { code: 'gl-ES', name: 'Galician' },
  { code: 'ka-GE', name: 'Georgian' },
  { code: 'de-DE', name: 'German' },
  { code: 'de-CH', name: 'German, Swiss' },
  { code: 'el-GR', name: 'Greek' },
  { code: 'gu-IN', name: 'Gujarati' },
  { code: 'ha-NG', name: 'Hausa' },
  { code: 'he-IL', name: 'Hebrew' },
  { code: 'hi-IN', name: 'Hindi, Indian' },
  { code: 'hu-HU', name: 'Hungarian' },
  { code: 'is-IS', name: 'Icelandic' },
  { code: 'id-ID', name: 'Indonesian' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'kab-DZ', name: 'Kabyle' },
  { code: 'kn-IN', name: 'Kannada' },
  { code: 'kk-KZ', name: 'Kazakh' },
  { code: 'rw-RW', name: 'Kinyarwanda' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'ky-KG', name: 'Kyrgyz' },
  { code: 'lv-LV', name: 'Latvian' },
  { code: 'lt-LT', name: 'Lithuanian' },
  { code: 'lg-IN', name: 'Luganda' },
  { code: 'mk-MK', name: 'Macedonian' },
  { code: 'ms-MY', name: 'Malay' },
  { code: 'ml-IN', name: 'Malayalam' },
  { code: 'mt-MT', name: 'Maltese' },
  { code: 'mr-IN', name: 'Marathi' },
  { code: 'mhr-RU', name: 'Meadow Mari' },
  { code: 'mn-MN', name: 'Mongolian' },
  { code: 'no-NO', name: 'Norwegian Bokmål' },
  { code: 'or-IN', name: 'Odia/Oriya' },
  { code: 'ps-AF', name: 'Pashto' },
  { code: 'pl-PL', name: 'Polish' },
  { code: 'pt-PT', name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese, Brazilian' },
  { code: 'pa-IN', name: 'Punjabi' },
  { code: 'ro-RO', name: 'Romanian' },
  { code: 'ru-RU', name: 'Russian' },
  { code: 'sr-RS', name: 'Serbian' },
  { code: 'si-LK', name: 'Sinhala' },
  { code: 'sk-SK', name: 'Slovak' },
  { code: 'sl-SI', name: 'Slovenian' },
  { code: 'so-SO', name: 'Somali' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'es-US', name: 'Spanish, US' },
  { code: 'su-ID', name: 'Sundanese' },
  { code: 'sw-KE', name: 'Swahili, Kenya' },
  { code: 'sw-BI', name: 'Swahili, Burundi' },
  { code: 'sw-RW', name: 'Swahili, Rwanda' },
  { code: 'sw-TZ', name: 'Swahili, Tanzania' },
  { code: 'sw-UG', name: 'Swahili, Uganda' },
  { code: 'sv-SE', name: 'Swedish' },
  { code: 'tl-PH', name: 'Tagalog/Filipino' },
  { code: 'ta-IN', name: 'Tamil' },
  { code: 'tt-RU', name: 'Tatar' },
  { code: 'te-IN', name: 'Telugu' },
  { code: 'th-TH', name: 'Thai' },
  { code: 'tr-TR', name: 'Turkish' },
  { code: 'uk-UA', name: 'Ukrainian' },
  { code: 'ug-CN', name: 'Uyghur' },
  { code: 'uz-UZ', name: 'Uzbek' },
  { code: 'vi-VN', name: 'Vietnamese' },
  { code: 'cy-WL', name: 'Welsh' },
  { code: 'wo-SN', name: 'Wolof' },
  { code: 'zu-ZA', name: 'Zulu' },
];

type SoapNotesDialogProps = DialogProps & {
  previousSoapNotes?: string;
  onSoapNotesChange: (notes: string) => void;
};

export const SoapNotesDialog = ({
  previousSoapNotes,
  onSoapNotesChange,
  open,
  onClose,
  ...rest
}: SoapNotesDialogProps) => {
  const { freeText } = useLLMDiagnose();
  const { patient, visit } = useWorkContext();
  const { breeds } = useBreedsContext();
  const { animalTypes } = useAnimalTypesContext();
  const {
    soapNotesMarkdown,
    isGenerating,
    setSoapNotes,
    setIsGenerating,
    soapNotesGeneratedAt,
    setSoapNotesGeneratedAt,
  } = useSoapNotes();
  const [copySuccess, setCopySuccess] = React.useState<boolean | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedMarkdown, setEditedMarkdown] = React.useState('');
  const [selectedLanguage, setSelectedLanguage] = React.useState<string>(() => {
    return localStorage.getItem('gekko/transcribe-language') || 'en-US';
  });

  const { selectedSymptoms, selectedDiagnoses, diagnoses } = useDiagnosesPage();
  const { symptoms, categories, symptomTypes } = useSymptoms();
  const { selectedTreatments, treatmentInfos, selectedTreatmentsObj } =
    useTreatmentsPage();
  const { selectedDrugDosages, treatmentDosages } = useDosagesPage();
  const {
    treatmentDurationPrefixes,
    doseTypes,
    dosageIntervals,
    treatmentDurations,
    summaryDrugUnits,
    summaryResultDrugUnits,
  } = useDoseParams();

  // Initialize context with previous notes if available
  useEffect(() => {
    if (previousSoapNotes && !soapNotesMarkdown) {
      setSoapNotes(previousSoapNotes);
    }
  }, [previousSoapNotes, soapNotesMarkdown, setSoapNotes]);

  // Initialize edited markdown when soap notes change
  useEffect(() => {
    if (soapNotesMarkdown && !isEditing) {
      setEditedMarkdown(soapNotesMarkdown);
    }
  }, [soapNotesMarkdown, isEditing]);

  const symptomsList = useMemo(() => {
    return selectedSymptoms.map((s) => {
      const symptom = symptoms.find((sy) => sy.Id === s);
      const parentSymptom = symptoms.find(
        (sy) => sy.Id === symptom?.MainSymptomId
      );
      const type = symptomTypes.find((t) => t.Id === symptom?.TypeId);
      const category = categories.find(
        (c) => c.Id === type?.SymptomTypeCategoryId
      );
      const symptomName =
        (parentSymptom?.Name ? `${parentSymptom.Name} - ` : '') + symptom?.Name;
      return `${category?.Name || 'Unknown category'} - ${
        type?.Name || 'Unknown type'
      } - ${symptomName}`;
    });
  }, [selectedSymptoms, symptoms, categories, symptomTypes]);

  const patientInfo = useMemo(() => {
    if (!patient) return { NameOrId: 'Unknown' };
    const genders = ['Unknown', 'Male', 'Female'];
    const breed = breeds.find((b) => b.Id === patient.BreedId);
    const animalType = animalTypes.find((at) => at.Id === patient.AnimalTypeId);
    const gender = genders[patient.GenderId] || 'Unknown';

    return {
      ...patient,
      breed: breed?.Name || 'Unknown breed',
      animalType: animalType?.Name || 'Unknown animal type',
      gender,
    };
  }, [patient, breeds, animalTypes]);

  const diagnoseList = useMemo(() => {
    return selectedDiagnoses.map((id) => {
      if (!diagnoses) return [];
      const diagnosis = diagnoses.find((d) => d.Id === id);
      if (!diagnosis) return { diseaseName: 'Unknown disease' };
      return {
        diseaseName: diagnosis.Disease.Name,
        info: diagnosis.Info,
        followUp: diagnosis.FollowUp,
        howtoConfirm: diagnosis.HowToConfirm,
        howtoExclude: diagnosis.HowToExclude,
      };
    });
  }, [selectedDiagnoses, diagnoses]);

  const treatmentList = useMemo(() => {
    if (!treatmentInfos) return [];
    return selectedTreatments.map((treatmentId) => {
      const treatment = selectedTreatmentsObj[treatmentId];
      const treatmentInfo = treatmentInfos.find((ti) =>
        ti.Treatments.some((t) => t.Id === treatmentId)
      );
      const drugDosageInfo =
        treatment.Type === TreatmentType.Drug &&
        getDrugDosages(
          treatment,
          selectedDrugDosages,
          treatmentDurationPrefixes,
          doseTypes,
          dosageIntervals,
          summaryDrugUnits,
          summaryResultDrugUnits,
          treatmentDurations,
          treatmentDosages
        );
      return {
        treatmentSetName: treatment.TreatmentSet.Name,
        treatmentName: treatment.Name || '',
        info: treatment.Info,
        followUp: treatment.FollowUp,
        generalTreatmentInfo: treatmentInfo?.Info || '',
        generalFollowUp: treatmentInfo?.FollowUp || '',
        drugDosageInfo,
      };
    });
  }, [
    selectedTreatments,
    treatmentInfos,
    selectedTreatmentsObj,
    selectedDrugDosages,
    treatmentDurationPrefixes,
    doseTypes,
    dosageIntervals,
    summaryDrugUnits,
    summaryResultDrugUnits,
    treatmentDurations,
    treatmentDosages,
  ]);

  const generateNotes = () => {
    setIsGenerating(true);
    API.postRequest<{ notes: string }>('/soap-notes/generate', {
      freetext: freeText,
      patient: patientInfo,
      visit: visit || { Date: new Date().toISOString() },
      symptoms: symptomsList,
      diagnoses: diagnoseList,
      treatments: treatmentList,
      previousNotes: soapNotesMarkdown,
      desiredLanguage: selectedLanguage,
    })
      .then((response) => {
        if (response.body?.notes) {
          setSoapNotes(response.body.notes);
          onSoapNotesChange(response.body.notes);
        }
        setSoapNotesGeneratedAt(new Date());
      })
      .catch((e) => {
        console.error(e);
        setSoapNotes('Error generating SOAP notes');
      })
      .finally(() => setIsGenerating(false));
  };

  useEffect(() => {
    if (!open) return;
    if (soapNotesMarkdown) return;
    generateNotes();
  }, [open, soapNotesMarkdown]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTimeAgo = () => {
    if (!soapNotesGeneratedAt) return '';
    return moment(soapNotesGeneratedAt).fromNow();
  };

  const downloadPdf = () => {
    // Clean up the markdown content to remove excessive whitespace
    const cleanedMarkdown = soapNotesMarkdown
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple newlines with double newlines
      .replace(/^\s+|\s+$/g, '') // Trim leading/trailing whitespace
      .replace(/[ \t]+$/gm, ''); // Remove trailing spaces on each line

    // Convert cleaned markdown to HTML
    const htmlContent = marked.parse(cleanedMarkdown);

    // Create a styled container with better page break handling
    const container = document.createElement('div');
    container.innerHTML = `
      <div class="pdf-container">
        <h1>SOAP Notes - ${patientInfo.NameOrId}</h1>
        <p>Date: ${new Date(visit?.Date || new Date()).toLocaleDateString()}</p>
        <div class="content">${htmlContent}</div>
      </div>
    `;

    // Add comprehensive styles for better PDF rendering
    const style = document.createElement('style');
    style.textContent = `
      .pdf-container {
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.4;
        color: #333;
        max-width: 100%;
        word-wrap: break-word;
      }
      
      .pdf-container h1 {
        font-size: 18px;
        margin-bottom: 10px;
        page-break-after: avoid;
      }
      
      .pdf-container h2 {
        font-size: 16px;
        margin-top: 15px;
        margin-bottom: 8px;
        page-break-after: avoid;
      }
      
      .pdf-container h3 {
        font-size: 14px;
        margin-top: 12px;
        margin-bottom: 6px;
        page-break-after: avoid;
      }
      
      .pdf-container p {
        margin-bottom: 8px;
      }
      
      /* Handle empty paragraphs and excessive spacing */
      .pdf-container p:empty {
        margin: 0;
        height: 0;
        display: none;
      }
      
      .pdf-container br + br {
        display: none;
      }
      
      .pdf-container h1 + p:empty,
      .pdf-container h2 + p:empty,
      .pdf-container h3 + p:empty,
      .pdf-container h4 + p:empty,
      .pdf-container h5 + p:empty,
      .pdf-container h6 + p:empty {
        display: none;
      }
      
      .pdf-container ul, .pdf-container ol {
        margin: 8px 0 12px 0;
        padding-left: 30px;
      }
      
      .pdf-container ul {
        list-style-type: disc;
        list-style-position: outside;
      }
      
      .pdf-container ol {
        list-style-type: decimal;
        list-style-position: outside;
      }
      
      .pdf-container ul ul {
        list-style-type: circle;
        margin-top: 4px;
        margin-bottom: 4px;
        padding-left: 25px;
      }
      
      .pdf-container ul ul ul {
        list-style-type: square;
        padding-left: 25px;
      }
      
      .pdf-container ol ol {
        list-style-type: lower-alpha;
        margin-top: 4px;
        margin-bottom: 4px;
        padding-left: 25px;
      }
      
      .pdf-container ol ol ol {
        list-style-type: lower-roman;
        padding-left: 25px;
      }
      
      .pdf-container li {
        margin-bottom: 4px;
        line-height: 1.5;
        padding-left: 8px;
      }
      
      .pdf-container li p {
        margin: 0;
        display: inline;
      }
      
      .pdf-container blockquote {
        margin: 10px 0;
        padding-left: 15px;
        border-left: 3px solid #ddd;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      .pdf-container table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 15px;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      .pdf-container th, .pdf-container td {
        border: 1px solid #ddd;
        padding: 6px;
        text-align: left;
      }
      
      .pdf-container th {
        background-color: #f5f5f5;
        font-weight: bold;
      }
      
      /* Prevent orphaned headers - keep headings with some following content */
      .pdf-container h1, .pdf-container h2, .pdf-container h3, 
      .pdf-container h4, .pdf-container h5, .pdf-container h6 {
        page-break-after: avoid;
        orphans: 2;
        widows: 2;
      }
    `;

    container.appendChild(style);

    // Enhanced PDF generation options
    const opt = {
      margin: [15, 15, 15, 15],
      filename: `SOAP_Notes_${patientInfo.NameOrId}_${
        new Date().toISOString().split('T')[0]
      }.pdf`,
      image: {
        type: 'jpeg',
        quality: 0.98,
      },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        allowTaint: false,
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy'],
        after: '.page-break-after',
        avoid: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'table'],
      },
    };

    html2pdf().set(opt).from(container).save();
  };

  const printNotes = () => {
    // Clean up the markdown content to remove excessive whitespace
    const cleanedMarkdown = soapNotesMarkdown
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple newlines with double newlines
      .replace(/^\s+|\s+$/g, '') // Trim leading/trailing whitespace
      .replace(/[ \t]+$/gm, ''); // Remove trailing spaces on each line

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>SOAP Notes - ${patientInfo.NameOrId}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px;
                line-height: 1.4;
                color: #333;
              }
              .container { 
                max-width: 800px; 
                margin: 0 auto; 
              }
              h1 { 
                color: #333;
                page-break-after: avoid;
              }
              h2, h3, h4, h5, h6 {
                page-break-after: avoid;
                margin-top: 15px;
                margin-bottom: 8px;
              }
              
              /* Keep headings with following content */
              h1 + *, h2 + *, h3 + *,
              h4 + *, h5 + *, h6 + * {
                page-break-before: avoid;
                break-before: avoid;
              }
              
              p {
                margin-bottom: 8px;
              }
              
              /* Handle empty paragraphs and excessive spacing */
              p:empty {
                margin: 0;
                height: 0;
                display: none;
              }
              
              br + br {
                display: none;
              }
              
              ul, ol {
                margin: 8px 0 12px 0;
                padding-left: 30px;
              }
              
              ul {
                list-style-type: disc;
                list-style-position: outside;
              }
              
              ol {
                list-style-type: decimal;
                list-style-position: outside;
              }
              
              ul ul {
                list-style-type: circle;
                margin-top: 4px;
                margin-bottom: 4px;
                padding-left: 25px;
              }
              
              ul ul ul {
                list-style-type: square;
                padding-left: 25px;
              }
              
              ol ol {
                list-style-type: lower-alpha;
                margin-top: 4px;
                margin-bottom: 4px;
                padding-left: 25px;
              }
              
              ol ol ol {
                list-style-type: lower-roman;
                padding-left: 25px;
              }
              
              li {
                margin-bottom: 4px;
                line-height: 1.5;
                padding-left: 8px;
              }
              
              li p {
                margin: 0;
                display: inline;
              }
              blockquote {
                page-break-inside: avoid;
                break-inside: avoid;
                margin: 10px 0;
                padding-left: 15px;
                border-left: 3px solid #ddd;
              }
              table {
                page-break-inside: avoid;
                break-inside: avoid;
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 15px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 6px;
                text-align: left;
              }
              th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              @media print {
                .no-print { display: none; }
                /* Additional print-specific styles */
                h1, h2, h3, h4, h5, h6 {
                  orphans: 3;
                  widows: 3;
                }
                p, li {
                  orphans: 2;
                  widows: 2;
                }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="no-print">
                <hr>
                <p>If printing doesn't start automatically, please click the Print button below.</p>
                <button onclick="window.print()">Print</button>
                <button onclick="window.close()">Close</button>
              </div>
              <h1>SOAP Notes - ${patientInfo.NameOrId}</h1>
              <p>Date: ${new Date(
                visit?.Date || new Date()
              ).toLocaleDateString()}</p>
              ${marked.parse(cleanedMarkdown)}
              
            </div>
            <script>
              // Attempt to print automatically after the page has loaded
              window.onload = function() {
                // Small delay to ensure content is fully rendered
                setTimeout(function() {
                  window.print();
                  // Try to close the window after printing dialog is closed
                  // Note: This may not work in all browsers due to security restrictions
                  window.addEventListener('afterprint', function() {
                    window.close();
                  });
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const copyToClipboard = async () => {
    try {
      // Convert markdown to HTML
      const htmlContent = marked.parse(soapNotesMarkdown);

      // For Word compatibility specifically
      const wordCompatibleHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" 
            xmlns:w="urn:schemas-microsoft-com:office:word" 
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <title>SOAP Notes</title>
        <style>
          body { font-family: Calibri, Arial, sans-serif; }
          h1, h2, h3, h4, h5, h6 { margin-top: 12pt; margin-bottom: 6pt; }
          p { margin-top: 6pt; margin-bottom: 6pt; }
          table { border-collapse: collapse; }
          table, th, td { border: 1px solid #ddd; padding: 8px; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `;

      // Create a Blob with the HTML content
      const blob = new Blob([wordCompatibleHtml], { type: 'text/html' });

      // Create a ClipboardItem with the HTML blob
      const item = new ClipboardItem({
        'text/html': blob,
        'text/plain': new Blob([soapNotesMarkdown], { type: 'text/plain' }),
      });

      // Write the ClipboardItem to clipboard
      await navigator.clipboard.write([item]);

      setCopySuccess(true);
      // Reset success message after 2 seconds
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy rich text: ', err);
      // Fall back to plain text if the HTML approach fails
      try {
        await navigator.clipboard.writeText(soapNotesMarkdown);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
        setCopySuccess(false);
        setTimeout(() => setCopySuccess(null), 2000);
      }
    }
  };

  const handleEditClick = () => {
    setEditedMarkdown(soapNotesMarkdown);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    setSoapNotes(editedMarkdown);
    onSoapNotesChange(editedMarkdown);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedMarkdown(soapNotesMarkdown);
    setIsEditing(false);
  };

  const handleLanguageChange = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    localStorage.setItem('gekko/transcribe-language', languageCode);
    // Trigger re-generation when language changes
    setSoapNotes('');
  };

  return (
    <Dialog open={open} {...rest} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        SOAP Notes
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="language-select-label">Language</InputLabel>
            <Select
              labelId="language-select-label"
              value={selectedLanguage}
              label="Language"
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={isGenerating || isEditing}
            >
              {awsTranscribeLanguages.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!isGenerating && soapNotesMarkdown && (
            <IconButton
              onClick={isEditing ? handleCancelEdit : handleEditClick}
              color={isEditing ? 'default' : 'primary'}
              size="small"
            >
              {isEditing ? <Cancel /> : <Edit />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>AI-Generated Content:</strong> This content was generated by
          artificial intelligence. Please review and verify all information
          before using in clinical practice.
        </Alert>

        {isGenerating ? (
          <Box sx={{ textAlign: 'center', marginTop: 3 }}>
            <CircularProgress />
          </Box>
        ) : isEditing ? (
          <Box>
            <TextField
              multiline
              fullWidth
              rows={20}
              value={editedMarkdown}
              onChange={(e) => setEditedMarkdown(e.target.value)}
              variant="outlined"
              placeholder="Edit your SOAP notes in Markdown format..."
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '14px',
                },
              }}
            />
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                startIcon={<Save />}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Markdown>{soapNotesMarkdown}</Markdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!isEditing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Button
                onClick={generateNotes}
                disabled={isGenerating}
                variant="contained"
                startIcon={
                  isGenerating ? <CircularProgress size={20} /> : <Refresh />
                }
              >
                Update notes
              </Button>
              {soapNotesGeneratedAt && (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  Last updated {getTimeAgo()}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download as PDF">
                <Button
                  onClick={downloadPdf}
                  variant="outlined"
                  disabled={isGenerating || !soapNotesMarkdown}
                  startIcon={<Download />}
                >
                  Download PDF
                </Button>
              </Tooltip>

              <Tooltip
                title={copySuccess === true ? 'Copied!' : 'Copy to clipboard'}
              >
                <Button
                  onClick={copyToClipboard}
                  variant="outlined"
                  disabled={isGenerating || !soapNotesMarkdown}
                  color={copySuccess === true ? 'success' : 'primary'}
                  startIcon={<ContentCopy />}
                >
                  {copySuccess === true ? 'Copied!' : 'Copy'}
                </Button>
              </Tooltip>

              <Tooltip title="Print notes">
                <Button
                  onClick={printNotes}
                  variant="outlined"
                  disabled={isGenerating || !soapNotesMarkdown}
                  startIcon={<Print />}
                >
                  Print
                </Button>
              </Tooltip>
            </Stack>
          </>
        )}

        <Button
          onClick={(ev) => onClose!(ev, 'escapeKeyDown')}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
